import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Flame, BookOpen, Target, Trophy, ChevronRight, Phone, MessageSquare, ShieldCheck, Calendar, Coins, TrendingUp, Star, ArrowRight, Compass, Play, Zap, Bot, FileText, X, Sparkles, Cpu, Brain, Sun, HardHat } from '../components/CustomIcons';
import ReentryTimeline from '../components/ReentryTimeline';
import StreakCalendar from '../components/StreakCalendar';
import OnboardingTour from '../components/OnboardingTour';
import WelcomeModal from '../components/WelcomeModal';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const LEVEL_TITLES = ['', 'Newcomer', 'Explorer', 'Student', 'Achiever', 'Scholar', 'Specialist', 'Dedicated Learner', 'Expert', 'Master', 'Trailblazer'];

/* ==========================================================
   Youth Learner Landing — lightweight welcome + CTA to the
   full Youth Portal experience over on /prototype.
   ========================================================== */
function YouthLearnerLanding({ user }) {
  const navigate = useNavigate();
  const profile = user?.profile || {};
  const xp = profile.xp || 0;
  const streak = profile.streak_days || 0;
  const badges = (profile.badges_earned || []).length;
  const goPortal = () => {
    try {
      localStorage.setItem('assistivos_active_role', 'youth_learner');
      localStorage.setItem('assistivos_demo_role', 'youth_learner');
    } catch (_e) { /* ignore */ }
    navigate('/prototype');
  };
  return (
    <div
      data-testid="youth-learner-dashboard"
      style={{
        padding: '32px 24px 48px',
        minHeight: '100vh',
        background: 'radial-gradient(1000px 500px at 20% 0%, rgba(249,115,22,0.10), transparent 60%), radial-gradient(800px 400px at 90% 100%, rgba(139,92,246,0.10), transparent 60%), var(--bg-page)',
      }}
    >
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <div style={{
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
          color: '#F97316', marginBottom: 12,
        }}>Youth Learner Portal</div>
        <h1 style={{
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontSize: 40, lineHeight: 1.05, margin: '0 0 12px 0',
          color: 'var(--text-primary)', letterSpacing: '-0.02em',
        }}>
          Welcome back, {user?.first_name || 'Jordan'}. <span style={{ color: '#F97316' }}>Let’s keep the streak alive.</span>
        </h1>
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 15, lineHeight: 1.6, margin: 0, maxWidth: 640,
          color: 'var(--text-secondary)',
        }}>
          You&apos;re building real skills through interactive quests, badges, and hands-on modules — right on your learning tablet.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, margin: '32px 0' }}>
          <YouthStat label="XP" value={xp} accent="#F97316" testId="youth-stat-xp" />
          <YouthStat label="Day streak" value={`${streak}🔥`} accent="#EF4444" testId="youth-stat-streak" />
          <YouthStat label="Badges earned" value={badges} accent="#8B5CF6" testId="youth-stat-badges" />
          <YouthStat label="Current course" value={(profile.current_pathway || 'solar power').replace(/_/g, ' ')} accent="#0EA5E9" small testId="youth-stat-course" />
        </div>

        <div
          style={{
            borderRadius: 20,
            background: 'linear-gradient(135deg, #0f2744 0%, #1a1200 100%)',
            padding: '28px 28px 32px',
            border: '1px solid rgba(249,115,22,0.35)',
            display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 24, fontWeight: 700, color: '#fafaf9', margin: '0 0 6px 0', letterSpacing: '-0.01em' }}>
              Continue your learning quest
            </div>
            <div style={{ fontSize: 13, color: 'rgba(250,250,249,0.7)', margin: 0, lineHeight: 1.5 }}>
              The full gamified experience — courses, mini-games, and rewards — lives in the Youth Portal on your tablet.
            </div>
          </div>
          <button
            onClick={goPortal}
            data-testid="youth-open-portal"
            style={{
              minHeight: 52, padding: '0 24px', borderRadius: 999, border: 0,
              background: '#F97316', color: '#0f2744',
              fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: 15,
              display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(249,115,22,0.35)',
            }}
          >
            Enter Youth Portal <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function YouthStat({ label, value, accent, small = false, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        borderRadius: 14,
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-default)',
        padding: '16px 18px',
      }}
    >
      <div style={{ fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif', fontWeight: 600, margin: '0 0 6px 0' }}>{label}</div>
      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: small ? 15 : 22, color: accent, lineHeight: 1.2, textTransform: small ? 'capitalize' : 'none' }}>
        {value}
      </div>
    </div>
  );
}

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, delay, ease: 'easeOut' }
});

/** Counts up from 0 to target over ~1.4s with easing */
function useCountUp(target) {
  const [count, setCount] = useState(0);
  const frame = useRef(null);
  useEffect(() => {
    if (!target) return;
    let start = 0;
    const duration = 1400;
    const startTime = performance.now();
    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setCount(Math.round(target * eased));
      if (progress < 1) frame.current = requestAnimationFrame(step);
    };
    frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [target]);
  return count;
}

function StatCard({ icon: Icon, value, label, color = '#CCCCCC' }) {
  return (
    <div className="card" style={{ padding: '16px', textAlign: 'center', flex: 1, minWidth: '120px' }}>
      <Icon size={18} style={{ color: color || '#F97316', marginBottom: '6px' }} />
      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '20px', color: 'var(--text-primary)' }}>{value}</div>
      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif' }}>{label}</div>
    </div>
  );
}

function CreditFloat({ credits, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 1600); return () => clearTimeout(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ position: 'fixed', top: '40%', left: '50%', transform: 'translateX(-50%)', zIndex: 1000, pointerEvents: 'none' }}
      className="animate-float-credit">
      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: '#F97316', textShadow: '0 0 20px rgba(249,115,22,0.8)' }}>+{credits} Credits</div>
    </div>
  );
}

const NEW_PRO_COURSES = [
  {
    key: 'robotics',
    title: 'Intro to Consumer AI-Robotics Repair',
    desc: 'Diagnose and repair AI-powered consumer devices — from smart home assistants to autonomous bots.',
    hours: 40, credential: 'CAIR Technician Cert.', Icon: Cpu, color: '#3B82F6', bg: 'rgba(59,130,246,0.1)',
  },
  {
    key: 'agentic',
    title: 'Agentic Modeling',
    desc: 'Design and deploy autonomous AI agents that plan, reason, and act across the next wave of automation.',
    hours: 30, credential: 'Agentic Systems Associate', Icon: Brain, color: '#8B5CF6', bg: 'rgba(139,92,246,0.1)',
  },
  {
    key: 'solar',
    title: 'Intermediate Photovoltaic Installation',
    desc: 'Install, commission, and maintain residential and commercial solar PV systems to NABCEP standards.',
    hours: 45, credential: 'NABCEP PV Associate', Icon: Sun, color: '#F59E0B', bg: 'rgba(245,158,11,0.1)',
  },
];

export default function Dashboard() {
  const { user, authFetch, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [recentActivity, setRecentActivity] = useState([]);
  const [pathwayData, setPathwayData] = useState(null);
  const [resumePosition, setResumePosition] = useState(null);
  const [floatCredit, setFloatCredit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDigest, setShowDigest] = useState(false);
  const [loginDates, setLoginDates] = useState([]);
  const [streakBonus, setStreakBonus] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [latestRecap, setLatestRecap] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const [myRank, setMyRank] = useState(null);
  const [newCoursesOpen, setNewCoursesOpen] = useState(
    () => localStorage.getItem('aos_new_courses_dismissed') !== '1'
  );

  const profile = user?.profile || {};
  const credits = profile.credits || 0;
  const streak = profile.streak_days || 0;
  const level = profile.level || 1;
  const xp = profile.xp || 0;
  const maxXp = profile.max_xp || 100;
  const releaseDate = profile.release_date;
  const daysToRelease = releaseDate ? Math.max(0, Math.round((new Date(releaseDate) - new Date()) / (1000 * 60 * 60 * 24))) : null;

  // Counting animation for credit balance
  const animatedCredits = useCountUp(loading ? 0 : credits);

  useEffect(() => {
    if (user?.role === 'learner') {
      // Daily check-in: award credits, track streak, update loginDates
      authFetch('/rewards/check-in', { method: 'POST' })
        .then(r => {
          if (r.data) {
            setLoginDates(r.data.login_dates || []);
            if (r.data.bonus_label && r.data.credits_earned > 5) {
              setStreakBonus(r.data);
            }
            // Refresh user context so sidebar credits stay in sync
            if (r.data.credits_earned > 0) {
              refreshUser();
            }
          }
        })
        .catch(() => {
          // Fallback: fetch streak data if check-in fails
          authFetch('/rewards/streak').then(r => setLoginDates(r.data?.login_dates || [])).catch(() => {});
        });

      Promise.all([
        authFetch('/rewards/history?limit=6'),
        authFetch('/courses/resume'),
      ]).then(([actRes, resumeRes]) => {
        setRecentActivity(actRes.data || []);
        const resume = resumeRes.data;
        setResumePosition(resume);

        // Fetch pathway data: prefer resume pathway > current_pathway
        const pathwayToFetch = resume?.pathway_id || profile.current_pathway;
        if (pathwayToFetch) {
          authFetch(`/courses/pathways/${pathwayToFetch}`)
            .then(r => {
              setPathwayData(r.data);
              // Show daily digest once per day
              const today = new Date().toISOString().slice(0, 10);
              const key = `digest_shown_${today}_${user._id}`;
              if (!sessionStorage.getItem(key)) {
                setShowDigest(true);
                // sessionStorage: UI-only preference flag, non-sensitive, resets on tab close
                sessionStorage.setItem(key, '1');
              }
            })
            .catch(() => {});
        }
      }).catch(() => {});

      // Fetch weekly summary
      authFetch('/rewards/weekly-summary')
        .then(r => setWeeklySummary(r.data))
        .catch(() => {});

      authFetch('/ai-tutor/recaps/latest')
        .then(r => setLatestRecap(r.data))
        .catch(() => {});

      // Leaderboard private rank (always fetched, even if opted out)
      authFetch('/gamification/leaderboard?metric=credits&limit=25')
        .then(r => { if (r.data?.current_user) setMyRank(r.data.current_user); })
        .catch(() => {});
    }
    setLoading(false);

    // Demo streak toast for learners
    if (user?.role === 'learner') {
      const toastKey = `streak_toast_${new Date().toISOString().slice(0,10)}_${user?._id}`;
      if (!sessionStorage.getItem(toastKey)) {
        const s = profile.streak_days || 0;
        if (s > 0) {
          setTimeout(() => toast(
            `${s}-day streak! Keep it going, ${user?.first_name || 'Learner'}.`,
            { icon: '🔥', duration: 4000 }
          ), 2000);
        }
        sessionStorage.setItem(toastKey, '1');
      }
      // New-courses toast — once per session
      const ncKey = `nc_toast_${user?._id}`;
      if (!sessionStorage.getItem(ncKey) && localStorage.getItem('aos_new_courses_dismissed') !== '1') {
        setTimeout(() => toast('3 new workforce courses just launched — AI-Robotics, Agentic Modeling & Solar PV!', {
          duration: 6000,
          action: { label: 'Explore', onClick: () => navigate('/learn') },
        }), 4000);
        sessionStorage.setItem(ncKey, '1');
      }
      // Show onboarding tour for first-time users
      if (!localStorage.getItem('aos_tour_done')) {
        setTimeout(() => setShowTour(true), 1500);
      }
    }

  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div style={{ padding: '32px', color: 'var(--text-muted)' }}>Loading...</div>;

  if (user?.role === 'youth_learner') {
    return <YouthLearnerLanding user={user} />;
  }

  const DAILY_CHALLENGES = [
    { id: 1, label: 'Log in and check your dashboard', credits: 5, done: true },
    { id: 2, label: 'Complete 2 lessons today', credits: 20, done: false },
    { id: 3, label: 'Pass a quiz with 80%+', credits: 30, done: false },
  ];

  return (
    <div style={{ padding: '28px 28px', background: 'var(--page-bg)', minHeight: '100vh' }} data-testid="learner-dashboard">
      {floatCredit && <CreditFloat credits={floatCredit} onDone={() => setFloatCredit(null)} />}

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* ── Page header ── */}
        <motion.div style={{ marginBottom: '24px' }} {...fade(0)}>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '22px', color: 'var(--text-headline)', margin: '0 0 4px 0' }}>
            Welcome back, {user?.first_name}
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--text-body)', margin: 0, fontFamily: 'Inter, sans-serif' }}>
            Day {profile.days_in || 1} on AssistivOS · Building something that lasts.
          </p>
        </motion.div>

        {/* ── 4 Metric cards ── */}
        <motion.div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '14px', marginBottom: '20px' }} {...fade(0.05)}>
          <div className="metric-card" data-accent="orange" data-testid="metric-credits" style={{ '--accent-color': 'var(--orange)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Coins size={15} color="var(--orange)" />
              <span className="text-section-lbl">Credits</span>
            </div>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '26px', color: 'var(--text-headline)', lineHeight: 1 }}>{(profile.credits || 0).toLocaleString()}</div>
            <div className="text-meta" style={{ marginTop: '4px' }}>total earned</div>
          </div>

          <div className="metric-card" data-accent="orange" data-testid="metric-streak">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Flame size={15} color="var(--orange)" />
              <span className="text-section-lbl">Streak</span>
            </div>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '26px', color: 'var(--text-headline)', lineHeight: 1 }}>{streak}</div>
            <div className="text-meta" style={{ marginTop: '4px' }}>day{streak !== 1 ? 's' : ''} in a row</div>
          </div>

          <div className="metric-card" data-accent="green" data-testid="metric-credentials">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <ShieldCheck size={15} color="var(--green)" />
              <span className="text-section-lbl">Credentials</span>
            </div>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '26px', color: 'var(--text-headline)', lineHeight: 1 }}>{user?.credential_count || 0}</div>
            <div className="text-meta" style={{ marginTop: '4px' }}>earned so far</div>
          </div>

          <div className="metric-card" data-accent="muted" data-testid="metric-pathway">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Target size={15} color="#64748B" />
              <span className="text-section-lbl">Pathway</span>
            </div>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '26px', color: 'var(--text-headline)', lineHeight: 1 }}>
              {profile.pathway_progress?.[profile.current_pathway] || 0}%
            </div>
            <div className="text-meta" style={{ marginTop: '4px' }}>complete</div>
          </div>
        </motion.div>

        {/* ── New Courses Alert Banner ── */}
        <AnimatePresence>
          {newCoursesOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              data-testid="new-courses-spotlight"
              style={{
                marginBottom: '20px', borderRadius: '12px', position: 'relative', overflow: 'hidden',
                background: '#0B1426', padding: '14px 18px',
                display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '240px' }}>
                <Sparkles size={14} color="#E87722" />
                <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px', color: '#FFFFFF' }}>3 new workforce courses just launched</span>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>AI-Robotics · Agentic Modeling · Solar PV</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button onClick={() => { try { localStorage.setItem('assistivos_active_role','professional_learner'); localStorage.setItem('assistivos_demo_role','professional_learner'); } catch(_e){} navigate('/prototype'); }} style={{ background: '#E87722', color: '#fff', border: 'none', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, cursor: 'pointer' }} data-testid="new-courses-explore-btn">Explore →</button>
                <button onClick={() => { setNewCoursesOpen(false); localStorage.setItem('aos_new_courses_dismissed','1'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: '4px', lineHeight: 0 }} data-testid="new-courses-dismiss"><X size={13}/></button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Daily Digest Banner ── */}
        <AnimatePresence>
          {showDigest && pathwayData && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              style={{ marginBottom: '20px', background: 'var(--surface)', border: '1px solid var(--border)', borderLeft: '3px solid var(--orange)', borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}
              data-testid="daily-digest-banner">
              <div style={{ flex: 1, minWidth: 200 }}>
                <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--text-headline)', margin: '0 0 3px 0' }}>
                  {streak > 0 ? `${streak}-day streak — keep it going!` : "Welcome back — let's keep building!"}
                </p>
                <p style={{ fontSize: '13px', color: 'var(--text-body)', margin: 0, fontFamily: 'Inter, sans-serif' }}>
                  You're <strong>{profile.pathway_progress?.[profile.current_pathway] || 0}%</strong> done with <strong>{pathwayData.name}</strong>
                </p>
              </div>
              <a href={resumePosition?.lesson_id ? `/learn/${resumePosition.pathway_id}/${resumePosition.course_id}/${resumePosition.lesson_id}` : `/learn/${profile.current_pathway}`}
                style={{ textDecoration: 'none', background: 'var(--orange)', color: '#fff', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700 }}
                data-testid="digest-continue-btn">Continue →</a>
              <button onClick={() => setShowDigest(false)} style={{ position: 'absolute', top: 8, right: 10, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px' }} aria-label="Dismiss" data-testid="digest-dismiss-btn"><X size={13}/></button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Counselor Recap Banner ── */}
        <AnimatePresence>
          {latestRecap?.has_recap && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ marginBottom: '20px', background: 'var(--surface)', border: '1px solid var(--border)', borderLeft: '3px solid var(--green)', borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}
              data-testid="dashboard-recap-card">
              <Bot size={16} color="var(--green)" />
              <div style={{ flex: 1 }}>
                <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', color: 'var(--text-headline)', margin: '0 0 2px 0' }}>Last Counselor Session: {latestRecap.title}</p>
                <p style={{ fontSize: '12px', color: 'var(--text-body)', margin: 0 }}>{latestRecap.next_step}</p>
              </div>
              <Link to="/ai-tutor" style={{ textDecoration: 'none', fontSize: '12px', color: 'var(--green-text)', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif', whiteSpace: 'nowrap' }}>Pick it back up →</Link>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Main two-column layout ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)', gap: '20px', alignItems: 'start' }}>

          {/* ── LEFT COLUMN ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Continue Learning */}
            <motion.div className="card" style={{ padding: '20px' }} {...fade(0.1)} data-testid="continue-learning-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-headline)', margin: 0 }}>Continue Learning</h2>
                <Link to={`/learn/${profile.current_pathway}`} style={{ fontSize: '12px', color: 'var(--orange)', fontWeight: 600, textDecoration: 'none', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>View pathway →</Link>
              </div>
              {pathwayData ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--orange-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <BookOpen size={18} color="var(--orange)" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-headline)' }}>{pathwayData.name}</div>
                      <div className="text-meta">
                        {profile.pathway_progress?.[profile.current_pathway] || 0}% complete
                      </div>
                    </div>
                  </div>
                  <div className="progress-track" style={{ marginBottom: '14px' }}>
                    <div className="progress-fill" style={{ width: `${profile.pathway_progress?.[profile.current_pathway] || 0}%` }} />
                  </div>
                  <a
                    href={resumePosition?.lesson_id ? `/learn/${resumePosition.pathway_id}/${resumePosition.course_id}/${resumePosition.lesson_id}` : `/learn/${profile.current_pathway}`}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--orange)', color: '#fff', borderRadius: '8px', padding: '10px 16px', textDecoration: 'none', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px', minHeight: 'var(--touch-min)' }}
                    data-testid="resume-learning-btn">
                    <Play size={14} /> Resume where you left off
                  </a>
                </div>
              ) : (
                <Link to="/learn" style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--orange)', color: '#fff', borderRadius: '8px', padding: '10px 16px', textDecoration: 'none', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px', minHeight: 'var(--touch-min)' }}>
                  <Compass size={14} /> Browse Career Pathways
                </Link>
              )}
            </motion.div>

            {/* Daily Challenges */}
            <motion.div className="card" style={{ padding: '20px' }} {...fade(0.15)} data-testid="daily-challenges-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-headline)', margin: 0 }}>Daily Challenges</h2>
                <span style={{ fontSize: '11px', background: 'var(--orange-subtle)', color: 'var(--orange)', borderRadius: '20px', padding: '2px 8px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>{DAILY_CHALLENGES.filter(c => c.done).length}/{DAILY_CHALLENGES.length} done</span>
              </div>
              {DAILY_CHALLENGES.map(c => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: c.done ? 'var(--green)' : 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {c.done && <span style={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>✓</span>}
                  </div>
                  <span style={{ flex: 1, fontSize: '13px', color: c.done ? 'var(--text-muted)' : 'var(--text-body)', textDecoration: c.done ? 'line-through' : 'none', fontFamily: 'Inter, sans-serif' }}>{c.label}</span>
                  <span style={{ fontSize: '12px', color: 'var(--orange)', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>+{c.credits}</span>
                </div>
              ))}
            </motion.div>

            {/* Quick Links */}
            <motion.div className="card" style={{ padding: '20px' }} {...fade(0.2)} data-testid="quick-links-card">
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-headline)', margin: '0 0 14px 0' }}>Quick Links</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {[
                  { to: '/ai-tutor', icon: Bot, label: 'AI Counselor', color: 'var(--orange)' },
                  { to: '/credentials', icon: ShieldCheck, label: 'My Credentials', color: 'var(--green)' },
                  { to: '/rewards', icon: Coins, label: 'Credits & Rewards', color: '#F59E0B' },
                  { to: '/community', icon: MessageSquare, label: 'Community', color: '#8B5CF6' },
                  { to: '/reentry', icon: Compass, label: 'Reentry Plan', color: '#3B82F6' },
                  { to: '/jobs', icon: FileText, label: 'Job Board', color: '#059669' },
                ].map(q => (
                  <Link key={q.to} to={q.to} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', background: 'var(--surface-alt)', border: '1px solid var(--border-subtle)', borderRadius: '10px', textDecoration: 'none', minHeight: 'var(--touch-min)', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
                  >
                    <q.icon size={15} color={q.color} />
                    <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '13px', fontWeight: 500, color: 'var(--text-body)' }}>{q.label}</span>
                  </Link>
                ))}
              </div>
            </motion.div>

          </div>

          {/* ── RIGHT COLUMN ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Streak heatmap (dark card) */}
            <motion.div style={{ background: '#0B1426', borderRadius: '14px', padding: '18px 20px' }} {...fade(0.1)} data-testid="streak-heatmap-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <Flame size={15} color="#E87722" />
                <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: '#FFFFFF' }}>{streak}-day streak</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px', marginBottom: '12px' }}>
                {Array.from({ length: 35 }).map((_, i) => {
                  const d = new Date(); d.setDate(d.getDate() - (34 - i));
                  const ds = d.toISOString().slice(0, 10);
                  const active = loginDates.includes(ds);
                  return (
                    <div key={i} title={ds} style={{ aspectRatio: '1', borderRadius: '4px', background: active ? '#E87722' : 'rgba(255,255,255,0.06)' }} />
                  );
                })}
              </div>
              {/* Level progress */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif' }}>Level {level} — {LEVEL_TITLES[level]}</span>
                  <span style={{ fontSize: '12px', color: '#E87722', fontWeight: 600 }}>{xp}/{maxXp} XP</span>
                </div>
                <div style={{ height: '5px', borderRadius: '3px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(xp/maxXp)*100}%`, background: '#E87722', borderRadius: '3px', transition: 'width 0.4s ease' }} />
                </div>
              </div>
            </motion.div>

            {/* Weekly summary */}
            {weeklySummary && (
              <motion.div className="card" style={{ padding: '18px 20px' }} {...fade(0.15)} data-testid="weekly-summary-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                  <TrendingUp size={15} color="var(--orange)" />
                  <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--text-headline)' }}>This Week</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                  <div style={{ textAlign: 'center', background: 'var(--surface-alt)', borderRadius: '10px', padding: '12px 8px' }}>
                    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '24px', color: 'var(--text-headline)' }}>{weeklySummary.lessons_this_week}</div>
                    <div className="text-meta">Lessons</div>
                  </div>
                  <div style={{ textAlign: 'center', background: 'var(--surface-alt)', borderRadius: '10px', padding: '12px 8px' }}>
                    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '24px', color: 'var(--orange)' }}>{weeklySummary.credits_this_week}</div>
                    <div className="text-meta">Credits</div>
                  </div>
                </div>
                {weeklySummary.next_certification && weeklySummary.lessons_remaining > 0 && (
                  <p style={{ fontSize: '12px', color: 'var(--text-body)', margin: 0 }}>
                    <span style={{ color: 'var(--orange)', fontWeight: 600 }}>{weeklySummary.lessons_remaining}</span> more lessons to <strong>{weeklySummary.next_certification}</strong>
                  </p>
                )}
              </motion.div>
            )}

            {/* Recent Activity */}
            <motion.div className="card" style={{ padding: '18px 20px' }} {...fade(0.2)} data-testid="activity-feed">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <TrendingUp size={15} color="var(--orange)" />
                <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--text-headline)' }}>Recent Activity</span>
              </div>
              {(recentActivity.length > 0 ? recentActivity : [
                { activity: 'Passed Quiz: Electrical Safety (92%)', type: 'earned', credits: 25, created_at: new Date().toISOString() },
                { activity: 'Completed: DC Circuit Fundamentals', type: 'earned', credits: 10, created_at: new Date(Date.now()-3600000).toISOString() },
                { activity: '7-Day Streak Bonus', type: 'earned', credits: 50, created_at: new Date(Date.now()-86400000).toISOString() },
              ]).slice(0, 5).map((a, i) => (
                <div key={a.id || i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                  <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: a.type === 'earned' ? 'var(--green)' : 'var(--danger)', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: '13px', color: 'var(--text-body)', fontFamily: 'Inter, sans-serif' }}>{a.activity}</span>
                  <span style={{ fontSize: '12px', color: a.type === 'earned' ? 'var(--green-text)' : 'var(--danger)', fontWeight: 600 }}>{a.type === 'earned' ? '+' : ''}{a.credits}</span>
                </div>
              ))}
            </motion.div>

            {/* Milestones */}
            <motion.div className="card" style={{ padding: '18px 20px' }} {...fade(0.25)} data-testid="upcoming-milestones-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <Trophy size={15} color="var(--orange)" />
                <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--text-headline)' }}>Upcoming Milestones</span>
              </div>
              {[
                { label: 'Next Certification', detail: 'NCCER Electrical Level 1', sub: '4 lessons away' },
                { label: 'Next Badge', detail: '14-Day Streak', sub: '2 days away' },
              ].map((m, i) => (
                <div key={m.label} style={{ display: 'flex', gap: '12px', paddingBottom: '12px', marginBottom: '12px', borderBottom: i < 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--orange)', marginTop: '6px', flexShrink: 0 }} />
                  <div>
                    <p className="text-meta" style={{ margin: '0 0 2px 0' }}>{m.label}</p>
                    <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', color: 'var(--text-headline)', margin: '0 0 2px 0' }}>{m.detail}</p>
                    <p style={{ fontSize: '12px', color: 'var(--orange)', margin: 0 }}>{m.sub}</p>
                  </div>
                </div>
              ))}
            </motion.div>

          </div>
        </div>

        {/* ── Reentry Timeline ── */}
        {daysToRelease !== null && (
          <motion.div style={{ marginTop: '24px' }} {...fade(0.3)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-headline)', margin: 0 }}>Reentry Timeline</h2>
              <Link to="/reentry" style={{ textDecoration: 'none', fontSize: '12px', color: 'var(--orange)', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Full View →</Link>
            </div>
            <ReentryTimeline releaseDate={releaseDate} daysRemaining={daysToRelease} />
          </motion.div>
        )}

      </div>
      {showTour && <OnboardingTour onComplete={() => setShowTour(false)} />}
      <WelcomeModal />
    </div>
  );
}
